import React, { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faPlus,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

const imageUrl = (path, apiBase) =>
    path
        ? `${apiBase}/images/Defect_image/${String(path).replace(/^\/+/, "")}`
        : `${apiBase}/images/Defect_image/add-image.jpg`;

const formatEnglishName = (value = "") => {
    const englishOnly = value.replace(/[^A-Za-z\s/&(),.'-]/g, "").replace(/^\s+/, "");
    return englishOnly
        ? `${englishOnly.charAt(0).toUpperCase()}${englishOnly.slice(1)}`
        : "";
};
const showProcessingAlert = (title) =>
    Swal.fire({
        title,
        text: "Please wait...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
    });
export default function DefectSetupSection({
    data,
    categoryForm,
    setCategoryForm,
    defectForm,
    setDefectForm,
    onSaveCategory,
    onSaveDefect,
    onDeleteCategory,
    onDeleteDefect,
    onReload,
    apiBase,
}) {
    const [mode, setMode] = useState("list");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [selectedCode, setSelectedCode] = useState(null);
    const selected = useMemo(
        () =>
            data.defects.find((item) => item.defect_code === selectedCode) ||
            null,
        [data.defects, selectedCode],
    );
    const [edit, setEdit] = useState(null);
    const [saving, setSaving] = useState(false);
    const [addImagePreview, setAddImagePreview] = useState("");
    const [addHasGroup, setAddHasGroup] = useState(false);

    const visibleDefects = useMemo(
        () =>
            data.defects.filter(
                (item) =>
                    !categoryFilter || item.product_category === categoryFilter,
            ),
        [data.defects, categoryFilter],
    );
    const openDetail = (item) => {
        setSelectedCode(item.defect_code);
        setEdit({
            code: item.defect_code,
            category: item.product_category,
            th: item.defect_desc_thai || "",
            en: item.defect_desc_eng || "",
            vn: item.defect_desc_vi || "",
            my: item.defect_desc_my || "",
            groupEn: item.defect_group_en || "",
            groupTh: item.defect_group_th || "",
            groupVn: item.defect_group_vn || "",
            groupMy: item.defect_group_my || "",
            image: null,
            imagePath: item.image_path || "",
        });
    };
    const saveEdit = async (event) => {
        event.preventDefault();
        setSaving(true);
        showProcessingAlert("Saving defect...");
        try {
            const form = new FormData();
            const payload = {
                code: edit.code,
                category: edit.category,
                th: edit.th,
                en: edit.en,
                vn: edit.vn,
                my: edit.my,
                group_en: formatEnglishName(edit.groupEn),
                group_th: edit.groupTh,
                group_vn: edit.groupVn,
                group_my: edit.groupMy,
            };
            Object.entries(payload).forEach(([key, value]) =>
                form.append(key, value ?? ""),
            );
            if (edit.image) form.append("image", edit.image);
            await axios.post(
                `${apiBase}/api/endline2/setup/update-defect`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            const savedImagePath = edit.image
                ? `${edit.code}.${edit.image.name.split(".").pop()}`
                : edit.imagePath;
            await onReload();
            setEdit((current) =>
                current
                    ? { ...current, image: null, imagePath: savedImagePath }
                    : current,
            );
            Swal.close();
            await Swal.fire({
                icon: "success",
                title: "Saved",
                timer: 900,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: "error",
                title: "Save failed",
                text: error.response?.data?.message || error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteDefect = async () => {
        const result = await Swal.fire({
            icon: "warning",
            title: `Delete ${edit.code}?`,
            text: "This removes the selected defect from the master list and Top 10 lists.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            confirmButtonColor: "#c83a3a",
        });
        if (!result.isConfirmed) return;
        try {
            await onDeleteDefect(edit.code);
            setSelectedCode(null);
            setEdit(null);
            await Swal.fire({
                icon: "success",
                title: "Defect deleted",
                timer: 900,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Delete failed",
                text: error.response?.data?.message || error.message,
            });
        }
    };

    const confirmDeleteCategory = async () => {
        if (!categoryForm.code) return;
        const result = await Swal.fire({
            icon: "warning",
            title: `Delete ${categoryForm.code}?`,
            text: "Categories that still contain defects cannot be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            confirmButtonColor: "#c83a3a",
        });
        if (!result.isConfirmed) return;
        try {
            await onDeleteCategory(categoryForm.code);
            await Swal.fire({
                icon: "success",
                title: "Category deleted",
                timer: 900,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Delete failed",
                text: error.response?.data?.message || error.message,
            });
        }
    };
    if (selected && edit) {
        const hasGroup = Boolean(
            edit.groupEn || edit.groupTh || edit.groupVn || edit.groupMy,
        );
        const updateEdit = (key, value) => setEdit({ ...edit, [key]: value });
        return (
            <section className="defect-detail-panel">
                <button
                    className="setup-text-button"
                    aria-label="Back to defect list"
                    data-tooltip="Back to defect list"
                    onClick={() => {
                        setSelectedCode(null);
                        setEdit(null);
                    }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <button
                    className="setup-delete-button"
                    type="button"
                    aria-label="Delete defect"
                    data-tooltip="Delete defect"
                    onClick={confirmDeleteDefect}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>{" "}
                <div className="defect-detail-heading">
                    <span>Defect detail</span>
                    <h2>{edit.code}</h2>
                </div>
                <div className="defect-detail-layout">
                    <div className="defect-detail-visual">
                        <div className="defect-detail-image">
                            <img
                                src={
                                    edit.image
                                        ? URL.createObjectURL(edit.image)
                                        : imageUrl(edit.imagePath, apiBase)
                                }
                                alt={edit.code}
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = imageUrl(
                                        null,
                                        apiBase,
                                    );
                                }}
                            />
                        </div>
                        <label className="setup-image-change">
                            Change image
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                    updateEdit(
                                        "image",
                                        e.target.files[0] || null,
                                    )
                                }
                            />
                        </label>
                        <div className="defect-code-chip">{edit.code}</div>
                    </div>
                    <form className="defect-detail-form" onSubmit={saveEdit}>
                        <div className="detail-form-grid">
                            <label>
                                Category
                                <select
                                    value={edit.category}
                                    onChange={(e) =>
                                        updateEdit("category", e.target.value)
                                    }
                                >
                                    {data.categories.map((item) => (
                                        <option
                                            key={item.code}
                                            value={item.code}
                                        >
                                            {item.th || item.en}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Defect name (Thai)
                                <input
                                    required
                                    value={edit.th}
                                    onChange={(e) =>
                                        updateEdit("th", e.target.value)
                                    }
                                />
                            </label>
                            <label>
                                Defect name (English)
                                <input
                                    required
                                    value={edit.en}
                                    onChange={(e) =>
                                        updateEdit("en", formatEnglishName(e.target.value))
                                    }
                                />
                            </label>
                            <label>
                                Defect name (Vietnamese)
                                <input
                                    value={edit.vn}
                                    onChange={(e) =>
                                        updateEdit("vn", e.target.value)
                                    }
                                />
                            </label>
                            <label>
                                Defect name (Myanmar)
                                <input
                                    value={edit.my}
                                    onChange={(e) =>
                                        updateEdit("my", e.target.value)
                                    }
                                />
                            </label>
                        </div>
                        <div
                            className={`group-editor ${hasGroup ? "has-group" : ""}`}
                        >
                            <div className="group-editor-head">
                                <div>
                                    <strong>Defect group</strong>
                                    <small>
                                        {hasGroup
                                            ? "Existing group"
                                            : "No group assigned"}
                                    </small>
                                </div>
                                {!hasGroup && (
                                    <button
                                        type="button"
                                        className="group-add-button"
                                        onClick={() =>
                                            updateEdit("groupEn", " ")
                                        }
                                    >
                                        + Add group
                                    </button>
                                )}
                            </div>
                            {hasGroup && (
                                <div className="detail-form-grid">
                                    <label>
                                        Group (English)
                                        <input
                                            value={edit.groupEn}
                                            onChange={(e) =>
                                                updateEdit(
                                                    "groupEn",
                                                    formatEnglishName(e.target.value),
                                                )
                                            }
                                        />
                                    </label>
                                    <label>
                                        Group (Thai)
                                        <input
                                            value={edit.groupTh}
                                            onChange={(e) =>
                                                updateEdit(
                                                    "groupTh",
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </label>
                                    <label>
                                        Group (Vietnamese)
                                        <input
                                            value={edit.groupVn}
                                            onChange={(e) =>
                                                updateEdit(
                                                    "groupVn",
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </label>
                                    <label>
                                        Group (Myanmar)
                                        <input
                                            value={edit.groupMy}
                                            onChange={(e) =>
                                                updateEdit(
                                                    "groupMy",
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                        <button className="setup-primary" disabled={saving}>
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                    </form>
                </div>
            </section>
        );
    }
    return (
        <section className="defect-setup-section">
            <div className="setup-mode-tabs">
                <button
                    className={mode === "list" ? "active" : ""}
                    onClick={() => setMode("list")}
                >
                    Defect list
                </button>
                <button
                    className={mode === "add" ? "active" : ""}
                    onClick={() => setMode("add")}
                >
                    Add Defect
                </button>
                <button
                    className={mode === "category" ? "active" : ""}
                    onClick={() => setMode("category")}
                >
                    Add Category
                </button>
            </div>
            {mode === "list" && (
                <article className="setup-panel defect-list-panel">
                    <header className="defect-list-head">
                        <div>
                            <h2>Defect list</h2>
                            <span>{visibleDefects.length} items</span>
                        </div>
                        <label>
                            Category
                            <select
                                value={categoryFilter}
                                onChange={(e) =>
                                    setCategoryFilter(e.target.value)
                                }
                            >
                                <option value="">All categories</option>
                                {data.categories.map((item) => (
                                    <option key={item.code} value={item.code}>
                                        {item.th || item.en}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </header>
                    <div className="setup-defect-table">
                        {visibleDefects.map((item) => (
                            <button
                                className="setup-defect-row"
                                key={item.defect_code}
                                onClick={() => openDetail(item)}
                            >
                                <img
                                    src={imageUrl(item.image_path, apiBase)}
                                    alt=""
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = imageUrl(
                                            null,
                                            apiBase,
                                        );
                                    }}
                                />
                                <span className="defect-row-code">
                                    {item.defect_code}
                                </span>
                                <span className="defect-row-name">
                                    <strong>{item.defect_desc_thai}</strong>
                                    <small>{item.defect_desc_eng}</small>
                                </span>
                                <small>{item.product_category_thai}</small>
                            </button>
                        ))}
                    </div>
                </article>
            )}
            {mode === "category" && (
                <article className="setup-panel category-editor-panel">
                    <header className="category-editor-head">
                        <div>
                            <span>Defect master</span>
                            <h2>
                                {categoryForm.code
                                    ? "Edit category"
                                    : "Add category"}
                            </h2>
                        </div>
                        {categoryForm.code && (
                            <span className="category-code-chip">
                                {categoryForm.code}
                            </span>
                        )}
                    </header>
                    <form
                        className="category-editor-form"
                        onSubmit={onSaveCategory}
                    >
                        <div className="category-selection-row">
                            <label>
                                Existing category
                                <select
                                    value={categoryForm.code || ""}
                                    onChange={(e) => {
                                        const selectedItem =
                                            data.categories.find(
                                                (item) =>
                                                    item.code ===
                                                    e.target.value,
                                            );
                                        setCategoryForm(
                                            selectedItem
                                                ? {
                                                      code: selectedItem.code,
                                                      th: selectedItem.th || "",
                                                      en: selectedItem.en || "",
                                                      vn: selectedItem.vn || "",
                                                      my: selectedItem.my || "",
                                                  }
                                                : {
                                                      code: "",
                                                      th: "",
                                                      en: "",
                                                      vn: "",
                                                      my: "",
                                                  },
                                        );
                                    }}
                                >
                                    <option value="">Select category</option>
                                    {data.categories.map((item) => (
                                        <option
                                            key={item.code}
                                            value={item.code}
                                        >
                                            {item.code}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="category-new-button"
                                aria-label="New category"
                                data-tooltip="New category"
                                onClick={() =>
                                    setCategoryForm({
                                        code: "",
                                        th: "",
                                        en: "",
                                        vn: "",
                                        my: "",
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        </div>
                        <div className="detail-form-grid">
                            <label>
                                Thai name
                                <input
                                    required
                                    value={categoryForm.th}
                                    onChange={(e) =>
                                        setCategoryForm({
                                            ...categoryForm,
                                            th: e.target.value,
                                        })
                                    }
                                />
                            </label>
                            <label>
                                English name
                                <input
                                    required
                                    value={categoryForm.en}
                                    onChange={(e) =>
                                        setCategoryForm({
                                            ...categoryForm,
                                            en: formatEnglishName(e.target.value),
                                        })
                                    }
                                />
                            </label>
                            <label>
                                Vietnamese name
                                <input
                                    value={categoryForm.vn || ""}
                                    onChange={(e) =>
                                        setCategoryForm({
                                            ...categoryForm,
                                            vn: e.target.value,
                                        })
                                    }
                                />
                            </label>
                            <label>
                                Myanmar name
                                <input
                                    value={categoryForm.my || ""}
                                    onChange={(e) =>
                                        setCategoryForm({
                                            ...categoryForm,
                                            my: e.target.value,
                                        })
                                    }
                                />
                            </label>
                        </div>
                        <footer className="category-editor-actions">
                            <button className="setup-primary" type="submit">
                                Save category
                            </button>
                            {categoryForm.code && (
                                <button
                                    className="category-delete-button"
                                    type="button"
                                    onClick={confirmDeleteCategory}
                                >
                                    <FontAwesomeIcon icon={faTrash} /> Delete
                                    category
                                </button>
                            )}
                        </footer>
                    </form>
                </article>
            )}
            {mode === "add" && (
                <article className="setup-panel add-defect-panel">
                    <div className="add-defect-heading">
                        <span>Defect master</span>
                        <h2>Add Defect</h2>
                    </div>
                    <div className="add-defect-layout">
                        <div className="add-defect-visual">
                            <div className="defect-detail-image">
                                <img
                                    src={
                                        addImagePreview ||
                                        imageUrl(null, apiBase)
                                    }
                                    alt="Defect preview"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = imageUrl(
                                            null,
                                            apiBase,
                                        );
                                    }}
                                />
                            </div>
                            <label className="setup-image-change">
                                Choose image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0] || null;
                                        setDefectForm({
                                            ...defectForm,
                                            image: file,
                                        });
                                        setAddImagePreview(
                                            file
                                                ? URL.createObjectURL(file)
                                                : "",
                                        );
                                    }}
                                />
                            </label>
                        </div>
                        <form
                            className="defect-detail-form"
                            onSubmit={onSaveDefect}
                        >
                            <div className="detail-form-grid">
                                <label>
                                    Category
                                    <select
                                        required
                                        value={defectForm.category}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                category: e.target.value,
                                            })
                                        }
                                    >
                                        {data.categories.map((item) => (
                                            <option
                                                value={item.code}
                                                key={item.code}
                                            >
                                                {item.th || item.en}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Defect code
                                    <input
                                        required
                                        value={defectForm.code}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                code: e.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase(),
                                            })
                                        }
                                    />
                                </label>
                                <label>
                                    Defect name (Thai)
                                    <input
                                        required
                                        value={defectForm.th}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                th: e.target.value,
                                            })
                                        }
                                    />
                                </label>
                                <label>
                                    Defect name (English)
                                    <input
                                        required
                                        value={defectForm.en}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                en: formatEnglishName(e.target.value),
                                            })
                                        }
                                    />
                                </label>
                                <label>
                                    Defect name (Vietnamese)
                                    <input
                                        value={defectForm.vn || ""}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                vn: e.target.value,
                                            })
                                        }
                                    />
                                </label>
                                <label>
                                    Defect name (Myanmar)
                                    <input
                                        value={defectForm.my || ""}
                                        onChange={(e) =>
                                            setDefectForm({
                                                ...defectForm,
                                                my: e.target.value,
                                            })
                                        }
                                    />
                                </label>
                            </div>
                            <div
                                className={`group-editor ${addHasGroup ? "has-group" : ""}`}
                            >
                                <div className="group-editor-head">
                                    <div>
                                        <strong>Defect group</strong>
                                        <small>
                                            {addHasGroup
                                                ? "New group"
                                                : "No group assigned"}
                                        </small>
                                    </div>
                                    {!addHasGroup && (
                                        <button
                                            type="button"
                                            className="group-add-button"
                                            onClick={() => setAddHasGroup(true)}
                                        >
                                            + Add group
                                        </button>
                                    )}
                                </div>
                                {addHasGroup && (
                                    <div className="detail-form-grid">
                                        <label>
                                            Group (English)
                                            <input
                                                value={
                                                    defectForm.group_en || ""
                                                }
                                                onChange={(e) =>
                                                    setDefectForm({
                                                        ...defectForm,
                                                        group_en:
                                                            formatEnglishName(e.target.value),
                                                    })
                                                }
                                            />
                                        </label>
                                        <label>
                                            Group (Thai)
                                            <input
                                                value={
                                                    defectForm.group_th || ""
                                                }
                                                onChange={(e) =>
                                                    setDefectForm({
                                                        ...defectForm,
                                                        group_th:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                        <label>
                                            Group (Vietnamese)
                                            <input
                                                value={
                                                    defectForm.group_vn || ""
                                                }
                                                onChange={(e) =>
                                                    setDefectForm({
                                                        ...defectForm,
                                                        group_vn:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                        <label>
                                            Group (Myanmar)
                                            <input
                                                value={
                                                    defectForm.group_my || ""
                                                }
                                                onChange={(e) =>
                                                    setDefectForm({
                                                        ...defectForm,
                                                        group_my:
                                                            e.target.value,
                                                    })
                                                }
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                            <button className="setup-primary">
                                Save defect
                            </button>
                        </form>
                    </div>
                </article>
            )}
        </section>
    );
}
